import { proxy, transfer, wrap } from 'comlink';
const LOG_PREFIX = '[RtmpPublishManager]';
/**
 * Main-thread manager for RTMP publishing.
 * Spawns a dedicated Web Worker and forwards encoded frames via Comlink.
 * Supports multiple concurrent destinations with dynamic add/remove.
 *
 * Frames are forwarded by MediaManager's composite track listener
 * via forwardVideoFrame / forwardAudioFrame.
 */
export class RtmpPublishManager {
    worker = null;
    workerApi = null;
    destinationIds = new Set();
    statusCallback;
    setStatusCallback(callback) {
        this.statusCallback = callback;
        if (this.workerApi) {
            this.workerApi.setStatusCallback(proxy(callback));
        }
    }
    ensureWorker() {
        if (!this.workerApi) {
            this.worker = new Worker(new URL('./RtmpWorker.ts', import.meta.url), { type: 'module' });
            this.workerApi = wrap(this.worker);
            if (this.statusCallback) {
                this.workerApi.setStatusCallback(proxy(this.statusCallback));
            }
            this.workerApi.init();
            console.log(`${LOG_PREFIX} Worker spawned`);
        }
        return this.workerApi;
    }
    /**
     * Add a new RTMP destination. Can be called at any time.
     * Each destination uses its own proxy URL for WebSocket-to-TCP bridging.
     * Returns a destination ID.
     */
    async addDestination(rtmpUrl, proxyUrl) {
        const id = crypto.randomUUID();
        const api = this.ensureWorker();
        console.log(`${LOG_PREFIX} Adding destination ${id}`);
        await api.addDestination(id, rtmpUrl, proxyUrl);
        this.destinationIds.add(id);
        return id;
    }
    /**
     * Remove a single destination by ID.
     */
    async removeDestination(id) {
        if (!this.destinationIds.has(id)) {
            console.warn(`${LOG_PREFIX} Ignoring removeDestination for unknown id ${id}`);
            return;
        }
        console.log(`${LOG_PREFIX} Removing destination ${id}`);
        this.workerApi?.removeDestination(id);
        this.destinationIds.delete(id);
    }
    /**
     * Remove all destinations and terminate the worker.
     */
    async removeAllDestinations() {
        console.log(`${LOG_PREFIX} Removing all destinations`);
        await this.workerApi?.disconnectAll();
        this.destinationIds.clear();
        this.terminateWorker();
    }
    /**
     * Connect a MessagePort to the RTMP worker for receiving encoded video
     * frames directly from the RTMP CompositeFrameEncoderWorker (worker-to-worker).
     */
    connectVideoPort(port) {
        const api = this.ensureWorker();
        api.connectVideoPort(transfer(port, [port]));
    }
    /**
     * Forward an encoded video frame to the RTMP worker.
     * Called by MediaManager's composite track listener.
     */
    forwardVideoFrame(frame) {
        this.workerApi?.sendVideoFrame(frame);
    }
    /**
     * Forward an encoded audio frame to the RTMP worker.
     * Called by MediaManager's composite track listener.
     */
    forwardAudioFrame(frame) {
        this.workerApi?.sendAudioFrame(frame);
    }
    terminateWorker() {
        if (this.worker) {
            this.worker.terminate();
            this.worker = null;
            this.workerApi = null;
            console.log(`${LOG_PREFIX} Worker terminated`);
        }
    }
    getDestinationCount() {
        return this.destinationIds.size;
    }
}
